import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Crown,
  Sparkles,
  Calendar,
  CreditCard,
  ExternalLink,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Receipt,
  Check,
  History,
  Trash2,
  RotateCcw,
} from "lucide-react";
import { useAuthCtx } from "@/lib/auth-context";
import { useSubscription, formatBRL, useTransactions, useGoals, useCategories } from "@/lib/store";
import { supabase } from "@/integrations/supabase/client";
import { getPaddleEnvironment } from "@/lib/paddle";
import { PremiumDialog } from "@/components/PremiumDialog";
import { EmailPreferences } from "@/components/EmailPreferences";
import { AssinaturaSkeleton } from "@/components/skeletons";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/_app/assinatura")({
  head: () => ({
    meta: [
      { title: "Assinatura — Seu Bolso Tranquilo" },
      { name: "description", content: "Gerencie seu plano Premium: status, próxima cobrança, histórico de pagamentos e cancelamento — tudo num só lugar." },
      { property: "og:title", content: "Assinatura — Seu Bolso Tranquilo" },
      { property: "og:description", content: "Gerencie seu plano Premium, cobranças e histórico de pagamentos." },
      { name: "robots", content: "noindex, follow" },
    ],
    links: [{ rel: "canonical", href: "https://seubolsotranquilo.com/assinatura" }],
  }),
  component: AssinaturaPage,
  pendingComponent: AssinaturaSkeleton,
});

const STATUS_LABEL: Record<string, { label: string; tone: "success" | "warning" | "danger" | "muted" }> = {
  active: { label: "Ativa", tone: "success" },
  trialing: { label: "Em teste grátis", tone: "success" },
  past_due: { label: "Pagamento pendente", tone: "warning" },
  paused: { label: "Pausada", tone: "warning" },
  canceled: { label: "Cancelada", tone: "danger" },
};

const PRICE_INFO: Record<string, { label: string; price: string; cycle: string }> = {
  premium_monthly: { label: "Premium Mensal", price: formatBRL(15), cycle: "por mês" },
  premium_yearly: { label: "Premium Anual", price: formatBRL(160), cycle: "por ano" },
};

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

type AuditEntry = {
  id: string;
  action: string;
  source: string;
  environment: string | null;
  old_status: string | null;
  new_status: string | null;
  old_plan: string | null;
  new_plan: string | null;
  old_period_end: string | null;
  new_period_end: string | null;
  old_cancel_at_period_end: boolean | null;
  new_cancel_at_period_end: boolean | null;
  changed_fields: string[];
  created_at: string;
};

const FIELD_LABEL: Record<string, string> = {
  status: "Status",
  plan: "Plano",
  current_period_start: "Início do período",
  current_period_end: "Fim do período",
  cancel_at_period_end: "Cancelamento agendado",
};

const ACTION_LABEL: Record<string, { label: string; tone: string }> = {
  insert: { label: "Assinatura criada", tone: "bg-success/15 text-success border-success/30" },
  update: { label: "Assinatura atualizada", tone: "bg-primary/15 text-primary-glow border-primary/30" },
  delete: { label: "Assinatura removida", tone: "bg-destructive/15 text-destructive border-destructive/30" },
};

function AssinaturaPage() {
  const { user, signOut } = useAuthCtx();
  const navigate = useNavigate();
  const { subscription, isPremium, loading } = useSubscription(user?.id);
  const [portalLoading, setPortalLoading] = useState<"manage" | "cancel" | "payment" | null>(null);
  const [audit, setAudit] = useState<AuditEntry[] | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [resetConfirmText, setResetConfirmText] = useState("");
  const { refresh: refreshTx } = useTransactions(user?.id);
  const { refresh: refreshGoals } = useGoals(user?.id);
  const { refresh: refreshCategories } = useCategories(user?.id);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    setAuditLoading(true);
    supabase
      .from("subscription_audit_log")
      .select(
        "id,action,source,environment,old_status,new_status,old_plan,new_plan,old_period_end,new_period_end,old_cancel_at_period_end,new_cancel_at_period_end,changed_fields,created_at"
      )
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(20)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error && data) setAudit(data as AuditEntry[]);
        else setAudit([]);
        setAuditLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, subscription?.id, subscription?.status]);

  const openPortal = async (action: "manage" | "cancel" | "payment") => {
    setPortalLoading(action);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal", {
        body: { environment: getPaddleEnvironment() },
      });
      if (error || !data) throw error ?? new Error("Sem resposta do servidor");
      const url =
        action === "cancel"
          ? data.cancelUrl
          : action === "payment"
            ? data.updatePaymentMethodUrl
            : data.overviewUrl;
      if (!url) {
        toast.error("Link indisponível no momento. Tente novamente em instantes.");
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error(e);
      toast.error("Não conseguimos abrir o portal. Tente novamente.");
    } finally {
      setPortalLoading(null);
    }
  };

  const resetData = async () => {
    if (!user?.id || resetting) return;
    setResetting(true);
    try {
      const [tx, goals, cats, recurring] = await Promise.all([
        supabase.from("transactions").delete().eq("user_id", user.id),
        supabase.from("goals").delete().eq("user_id", user.id),
        supabase.from("categories").delete().eq("user_id", user.id),
        supabase.from("recurring_transactions").delete().eq("user_id", user.id),
      ]);
      const err = tx.error || goals.error || cats.error || recurring.error;
      if (err) throw err;
      await Promise.all([refreshTx(), refreshGoals(), refreshCategories()]);
      toast.success("Seus dados foram apagados.");
    } catch (e) {
      console.error(e);
      toast.error("Não foi possível resetar seus dados. Tente novamente.");
    } finally {
      setResetting(false);
    }
  };

  const deleteAccount = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      const { error } = await supabase.functions.invoke("delete-account", { body: {} });
      if (error) throw error;
      toast.success("Conta excluída. Até logo!");
      await signOut();
      navigate({ to: "/" });
    } catch (e) {
      console.error(e);
      toast.error("Não conseguimos excluir sua conta. Tente novamente.");
      setDeleting(false);
    }
  };

  const status = subscription?.status ?? "active";
  const statusInfo =
    isPremium && STATUS_LABEL[status]
      ? STATUS_LABEL[status]
      : { label: "Plano gratuito", tone: "muted" as const };
  const priceInfo = subscription?.priceId ? PRICE_INFO[subscription.priceId] : null;
  const periodEnd = subscription?.currentPeriodEnd ?? null;
  const willCancel = !!subscription?.cancelAtPeriodEnd && status !== "canceled";
  const isCanceledWithGrace =
    status === "canceled" && periodEnd && new Date(periodEnd).getTime() > Date.now();

  if (loading) return <AssinaturaSkeleton />;

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto space-y-6 animate-in fade-in duration-300">
      <header>
        <p className="text-sm text-muted-foreground">Sua conta</p>
        <h1 className="font-display text-3xl lg:text-4xl font-bold mt-1">Assinatura</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Gerencie seu plano, pagamento e histórico de faturas.
        </p>
      </header>

      <>
          {/* Status card */}
          <section
            className={cn(
              "rounded-3xl border p-6 lg:p-8",
              isPremium
                ? "bg-gradient-to-br from-primary/15 via-primary/5 to-transparent border-primary/30"
                : "bg-gradient-card border-border/40"
            )}
          >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex items-start gap-4 min-w-0">
                <div
                  className={cn(
                    "shrink-0 size-12 rounded-2xl flex items-center justify-center",
                    isPremium
                      ? "bg-gradient-primary shadow-glow"
                      : "bg-muted/60 border border-border/40"
                  )}
                >
                  {isPremium ? (
                    <Crown className="size-5 text-primary-foreground" />
                  ) : (
                    <Sparkles className="size-5 text-muted-foreground" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                    Plano atual
                  </p>
                  <h2 className="font-display text-2xl font-bold mt-1">
                    {priceInfo?.label ?? (isPremium ? "Premium" : "Gratuito")}
                  </h2>
                  {priceInfo && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {priceInfo.price}{" "}
                      <span className="text-xs">{priceInfo.cycle}</span>
                    </p>
                  )}
                </div>
              </div>

              <span
                className={cn(
                  "shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold",
                  statusInfo.tone === "success" &&
                    "bg-success/15 text-success border border-success/30",
                  statusInfo.tone === "warning" &&
                    "bg-warning/15 text-warning border border-warning/30",
                  statusInfo.tone === "danger" &&
                    "bg-destructive/15 text-destructive border border-destructive/30",
                  statusInfo.tone === "muted" &&
                    "bg-muted/60 text-muted-foreground border border-border/40"
                )}
              >
                {statusInfo.tone === "success" && <CheckCircle2 className="size-3.5" />}
                {statusInfo.tone === "warning" && <AlertTriangle className="size-3.5" />}
                {statusInfo.tone === "danger" && <XCircle className="size-3.5" />}
                {statusInfo.label}
              </span>
            </div>

            {/* Notices */}
            {status === "past_due" && (
              <div className="mt-5 rounded-2xl bg-warning/10 border border-warning/30 px-4 py-3 text-sm text-warning flex items-start gap-2">
                <AlertTriangle className="size-4 mt-0.5 shrink-0" />
                <p>
                  Não conseguimos processar seu último pagamento. Atualize seu cartão para
                  manter o acesso ao Premium.
                </p>
              </div>
            )}
            {willCancel && periodEnd && (
              <div className="mt-5 rounded-2xl bg-warning/10 border border-warning/30 px-4 py-3 text-sm text-warning flex items-start gap-2">
                <AlertTriangle className="size-4 mt-0.5 shrink-0" />
                <p>
                  Sua assinatura foi cancelada e permanecerá ativa até{" "}
                  <strong>{formatDate(periodEnd)}</strong>. Após essa data, você voltará ao
                  plano gratuito.
                </p>
              </div>
            )}
            {isCanceledWithGrace && (
              <div className="mt-5 rounded-2xl bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive flex items-start gap-2">
                <XCircle className="size-4 mt-0.5 shrink-0" />
                <p>
                  Assinatura cancelada. Seu acesso Premium continua válido até{" "}
                  <strong>{formatDate(periodEnd)}</strong>.
                </p>
              </div>
            )}

            {/* Period info */}
            {isPremium && periodEnd && (
              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                <div className="rounded-2xl bg-surface-elevated/40 border border-border/40 px-4 py-3 flex items-center gap-3">
                  <Calendar className="size-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">
                      {willCancel || status === "canceled"
                        ? "Acesso encerra em"
                        : "Próxima cobrança"}
                    </p>
                    <p className="font-medium text-sm truncate">{formatDate(periodEnd)}</p>
                  </div>
                </div>
                {subscription?.id && (
                  <div className="rounded-2xl bg-surface-elevated/40 border border-border/40 px-4 py-3 flex items-center gap-3">
                    <Receipt className="size-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">Forma de pagamento</p>
                      <p className="font-medium text-sm truncate">
                        Gerenciada pela Paddle
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Actions */}
          {isPremium ? (
            <section className="rounded-3xl bg-gradient-card border border-border/40 p-6 lg:p-8 space-y-4">
              <div>
                <h3 className="font-display text-lg font-semibold">Gerenciar assinatura</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Atualize seu cartão, baixe faturas ou cancele a qualquer momento. Os links
                  abrem o portal seguro da Paddle em uma nova aba.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <button
                  onClick={() => openPortal("manage")}
                  disabled={portalLoading !== null}
                  className="group inline-flex items-center justify-between gap-3 rounded-2xl border border-border/50 bg-surface-elevated/40 px-4 py-3.5 text-left hover:border-primary/40 hover:bg-primary/5 transition-all disabled:opacity-60"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="shrink-0 size-9 rounded-xl bg-primary/15 flex items-center justify-center">
                      <Receipt className="size-4 text-primary-glow" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm">Faturas e dados</p>
                      <p className="text-xs text-muted-foreground truncate">
                        Histórico de pagamentos
                      </p>
                    </div>
                  </div>
                  {portalLoading === "manage" ? (
                    <Loader2 className="size-4 animate-spin text-muted-foreground" />
                  ) : (
                    <ExternalLink className="size-4 text-muted-foreground group-hover:text-foreground" />
                  )}
                </button>

                <button
                  onClick={() => openPortal("payment")}
                  disabled={portalLoading !== null}
                  className="group inline-flex items-center justify-between gap-3 rounded-2xl border border-border/50 bg-surface-elevated/40 px-4 py-3.5 text-left hover:border-primary/40 hover:bg-primary/5 transition-all disabled:opacity-60"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="shrink-0 size-9 rounded-xl bg-primary/15 flex items-center justify-center">
                      <CreditCard className="size-4 text-primary-glow" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm">Atualizar cartão</p>
                      <p className="text-xs text-muted-foreground truncate">
                        Trocar forma de pagamento
                      </p>
                    </div>
                  </div>
                  {portalLoading === "payment" ? (
                    <Loader2 className="size-4 animate-spin text-muted-foreground" />
                  ) : (
                    <ExternalLink className="size-4 text-muted-foreground group-hover:text-foreground" />
                  )}
                </button>
              </div>

              {!willCancel && status !== "canceled" && (
                <div className="pt-4 border-t border-border/40">
                  <button
                    onClick={() => openPortal("cancel")}
                    disabled={portalLoading !== null}
                    className="inline-flex items-center gap-2 text-sm text-destructive hover:underline disabled:opacity-60"
                  >
                    {portalLoading === "cancel" ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <XCircle className="size-4" />
                    )}
                    Cancelar assinatura
                  </button>
                  <p className="text-xs text-muted-foreground mt-1.5">
                    Você continuará Premium até o fim do período já pago.
                  </p>
                </div>
              )}
            </section>
          ) : (
            <section className="rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent p-6 lg:p-8 space-y-6">
              <div className="flex items-start gap-4">
                <div className="shrink-0 size-12 rounded-2xl bg-gradient-primary flex items-center justify-center shadow-glow">
                  <Sparkles className="size-5 text-primary-foreground" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-display text-xl font-bold">
                    Você ainda não tem uma assinatura ativa
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Você está no plano <strong>Gratuito</strong>. Faça upgrade para o
                    Premium e desbloqueie tudo — a partir de{" "}
                    <strong>R$ 13,33/mês</strong>.
                  </p>
                </div>
              </div>

              <ul className="grid sm:grid-cols-2 gap-2.5 text-sm">
                {[
                  "Transações ilimitadas",
                  "Metas e categorias ilimitadas",
                  "Assistente com IA",
                  "Previsões e insights avançados",
                ].map((feat) => (
                  <li key={feat} className="flex items-center gap-2">
                    <span className="shrink-0 size-5 rounded-full bg-primary/20 flex items-center justify-center">
                      <Check className="size-3 text-primary-glow" />
                    </span>
                    <span className="text-foreground/90">{feat}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <PremiumDialog
                  trigger={
                    <button
                      type="button"
                      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-gradient-primary text-primary-foreground font-semibold shadow-glow hover:opacity-90 transition-opacity"
                    >
                      <Sparkles className="size-4" /> Assinar Premium
                    </button>
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Cancele quando quiser. Sem fidelidade.
                </p>
              </div>
            </section>
          )}

          {/* Audit log — show only if the user has any history */}
          {(auditLoading || (audit && audit.length > 0)) && (
            <section className="rounded-3xl bg-gradient-card border border-border/40 p-6 lg:p-8 space-y-4">
              <div className="flex items-start gap-3">
                <div className="shrink-0 size-10 rounded-xl bg-muted/60 border border-border/40 flex items-center justify-center">
                  <History className="size-4 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-display text-lg font-semibold">
                    Histórico de mudanças
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Toda alteração na sua assinatura registrada pelo provedor de pagamento.
                  </p>
                </div>
              </div>

              {auditLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="size-4 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <ol className="space-y-3">
                  {audit!.map((entry) => {
                    const meta = ACTION_LABEL[entry.action] ?? {
                      label: entry.action,
                      tone: "bg-muted/60 text-muted-foreground border-border/40",
                    };
                    return (
                      <li
                        key={entry.id}
                        className="rounded-2xl border border-border/40 bg-surface-elevated/40 p-4"
                      >
                        <div className="flex flex-wrap items-center gap-2 justify-between">
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border",
                              meta.tone
                            )}
                          >
                            {meta.label}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatDateTime(entry.created_at)} · origem: {entry.source}
                            {entry.environment ? ` · ${entry.environment}` : ""}
                          </span>
                        </div>

                        {entry.changed_fields.length > 0 && (
                          <ul className="mt-3 space-y-1.5 text-sm">
                            {entry.changed_fields.map((field) => {
                              const label = FIELD_LABEL[field] ?? field;
                              let oldVal: string | null = null;
                              let newVal: string | null = null;
                              if (field === "status") {
                                oldVal = entry.old_status;
                                newVal = entry.new_status;
                              } else if (field === "plan") {
                                oldVal = entry.old_plan;
                                newVal = entry.new_plan;
                              } else if (field === "current_period_end") {
                                oldVal = entry.old_period_end ? formatDate(entry.old_period_end) : null;
                                newVal = entry.new_period_end ? formatDate(entry.new_period_end) : null;
                              } else if (field === "cancel_at_period_end") {
                                oldVal = entry.old_cancel_at_period_end ? "Sim" : "Não";
                                newVal = entry.new_cancel_at_period_end ? "Sim" : "Não";
                              }
                              return (
                                <li key={field} className="flex flex-wrap items-baseline gap-1.5 text-muted-foreground">
                                  <span className="font-medium text-foreground/90">{label}:</span>
                                  {oldVal !== null && (
                                    <span className="line-through opacity-70">{oldVal}</span>
                                  )}
                                  {oldVal !== null && newVal !== null && <span>→</span>}
                                  {newVal !== null && (
                                    <span className="text-foreground">{newVal}</span>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>
          )}

          {/* Help */}
          <p className="text-xs text-muted-foreground text-center">
            Pagamentos processados pela{" "}
            <a
              href="https://www.paddle.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              Paddle
            </a>
            , nosso revendedor oficial. Para suporte de cobrança, acesse{" "}
            <a
              href="https://paddle.net"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              paddle.net
            </a>
            .
          </p>

          <EmailPreferences />

          {/* Danger zone */}
          <section className="rounded-3xl border border-destructive/30 bg-destructive/5 p-6 lg:p-8 space-y-5">
            <div className="flex items-start gap-3">
              <div className="shrink-0 size-10 rounded-xl bg-destructive/15 border border-destructive/30 flex items-center justify-center">
                <AlertTriangle className="size-4 text-destructive" />
              </div>
              <div className="min-w-0">
                <h3 className="font-display text-lg font-semibold text-destructive">
                  Zona de risco
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Ações permanentes que não podem ser desfeitas. Leia com atenção
                  antes de confirmar.
                </p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              {/* Reset data */}
              <AlertDialog onOpenChange={(o) => !o && setResetConfirmText("")}>
                <AlertDialogTrigger asChild>
                  <button
                    type="button"
                    disabled={resetting || deleting}
                    className="group inline-flex items-center justify-between gap-3 rounded-2xl border border-border/50 bg-surface-elevated/40 px-4 py-3.5 text-left hover:border-warning/50 hover:bg-warning/5 transition-all disabled:opacity-60"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="shrink-0 size-9 rounded-xl bg-warning/15 flex items-center justify-center">
                        <RotateCcw className="size-4 text-warning" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm">Apagar dados financeiros</p>
                        <p className="text-xs text-muted-foreground truncate">
                          Mantém sua conta e assinatura
                        </p>
                      </div>
                    </div>
                    {resetting && (
                      <Loader2 className="size-4 animate-spin text-muted-foreground" />
                    )}
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Apagar todos os seus dados financeiros?</AlertDialogTitle>
                    <AlertDialogDescription asChild>
                      <div className="space-y-3 text-sm">
                        <p>Esta ação é <strong>permanente</strong> e não pode ser desfeita.</p>
                        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-3">
                          <p className="text-xs font-semibold uppercase tracking-wider text-destructive mb-1.5">
                            Será apagado
                          </p>
                          <ul className="list-disc pl-4 space-y-0.5 text-foreground/90">
                            <li>Todas as suas transações (receitas e despesas)</li>
                            <li>Todas as metas financeiras</li>
                            <li>Categorias personalizadas que você criou</li>
                          </ul>
                        </div>
                        <div className="rounded-2xl border border-success/30 bg-success/5 p-3">
                          <p className="text-xs font-semibold uppercase tracking-wider text-success mb-1.5">
                            Permanece intacto
                          </p>
                          <ul className="list-disc pl-4 space-y-0.5 text-foreground/90">
                            <li>Sua conta e e-mail de login</li>
                            <li>Sua assinatura Premium e cobranças</li>
                            <li>Histórico de pagamentos</li>
                          </ul>
                        </div>
                        <p>
                          Para confirmar, digite <strong>RESETAR</strong> abaixo.
                        </p>
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <input
                    autoFocus
                    value={resetConfirmText}
                    onChange={(e) => setResetConfirmText(e.target.value)}
                    placeholder="RESETAR"
                    className="w-full px-4 py-2.5 rounded-2xl bg-input border border-border/40 text-sm focus:border-warning focus:outline-none"
                  />
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={resetData}
                      disabled={resetConfirmText.trim() !== "RESETAR" || resetting}
                      className="bg-warning text-warning-foreground hover:bg-warning/90 disabled:opacity-50"
                    >
                      Apagar dados
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {/* Delete account */}
              <AlertDialog onOpenChange={(o) => !o && setConfirmText("")}>
                <AlertDialogTrigger asChild>
                  <button
                    type="button"
                    disabled={resetting || deleting}
                    className="group inline-flex items-center justify-between gap-3 rounded-2xl border border-destructive/40 bg-destructive/5 px-4 py-3.5 text-left hover:border-destructive/70 hover:bg-destructive/10 transition-all disabled:opacity-60"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="shrink-0 size-9 rounded-xl bg-destructive/15 flex items-center justify-center">
                        <Trash2 className="size-4 text-destructive" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm text-destructive">
                          Excluir minha conta
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          Remove conta, login e todos os dados
                        </p>
                      </div>
                    </div>
                    {deleting && (
                      <Loader2 className="size-4 animate-spin text-muted-foreground" />
                    )}
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Excluir sua conta para sempre?</AlertDialogTitle>
                    <AlertDialogDescription asChild>
                      <div className="space-y-3 text-sm">
                        <p>Esta ação é <strong>permanente</strong> e não pode ser desfeita.</p>
                        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-3">
                          <p className="text-xs font-semibold uppercase tracking-wider text-destructive mb-1.5">
                            Será apagado
                          </p>
                          <ul className="list-disc pl-4 space-y-0.5 text-foreground/90">
                            <li>Sua conta e acesso ao app</li>
                            <li>Todas as transações, metas e categorias</li>
                            <li>Suas preferências e configurações</li>
                          </ul>
                        </div>
                        {isPremium && (
                          <div className="rounded-2xl border border-warning/30 bg-warning/5 p-3">
                            <p className="text-xs font-semibold uppercase tracking-wider text-warning mb-1.5">
                              Atenção: assinatura ativa
                            </p>
                            <p className="text-foreground/90">
                              Excluir a conta <strong>não cancela</strong> sua assinatura
                              Premium automaticamente. Cancele antes pelo portal de pagamento
                              para evitar novas cobranças.
                            </p>
                          </div>
                        )}
                        <p>
                          Para confirmar, digite <strong>EXCLUIR</strong> abaixo.
                        </p>
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <input
                    autoFocus
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="EXCLUIR"
                    className="w-full px-4 py-2.5 rounded-2xl bg-input border border-border/40 text-sm focus:border-destructive focus:outline-none"
                  />
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={deleteAccount}
                      disabled={confirmText.trim() !== "EXCLUIR" || deleting}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
                    >
                      Excluir conta para sempre
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </section>
        </>
    </div>
  );
}